import { useState } from 'react';
import { FiCheck, FiCopy, FiLink } from 'react-icons/fi';

import { useGenerateInviteLink } from '@/store/invites/invites.store';
import { toast } from '@/utils/toast';

import s from './InviteLinkButton.module.scss';

const COPIED_MS = 2000;

interface InviteLinkButtonProps {
  eventId: string;
  eventName: string;
}

export function InviteLinkButton({ eventId, eventName }: InviteLinkButtonProps) {
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { mutateAsync, isPending } = useGenerateInviteLink();

  const generate = async () => {
    try {
      const invite = await mutateAsync({
        event: { id: eventId, name: eventName },
        service_type: 'vessel',
      });
      if (!invite?.link) {
        toast.error('Couldn’t generate the invite link. Please try again.');
        return;
      }
      setLink(invite.link);
    } catch {
      toast.error('Couldn’t generate the invite link. Please try again.');
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      toast.error('Couldn’t copy the link. Please try again.');
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), COPIED_MS);
  };

  if (link) {
    return (
      <button type="button" className={s.inviteLinkButton} onClick={copy}>
        <span className={s.inviteLinkIcon}>{copied ? <FiCheck /> : <FiCopy />}</span>
        {copied ? 'Link Copied' : 'Copy Link'}
      </button>
    );
  }

  return (
    <button type="button" className={s.inviteLinkButton} onClick={generate} disabled={isPending}>
      <span className={s.inviteLinkIcon}>
        <FiLink />
      </span>
      {isPending ? 'Generating…' : 'Generate Invite Link'}
    </button>
  );
}
